import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Saved products (the heart). A product is saved with all its sizes, so we
// keep the saved products' family ids and fill the heart on any size.
const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => new Set());
  const isCustomer = user?.role === "customer";

  const reload = useCallback(async () => {
    if (!isCustomer) { setIds(new Set()); return; }
    try {
      const { data } = await api.get("/customer/wishlist/ids");
      setIds(new Set(data || []));
    } catch { /* keep what we have */ }
  }, [isCustomer]);
  useEffect(() => { reload(); }, [reload]);

  const value = useMemo(() => ({
    ids, isCustomer, reload,
    has: (p) => !!p && ids.has(p.family_id || p.id),
    setSaved: (fid, on) => setIds((cur) => { const n = new Set(cur); if (on) n.add(fid); else n.delete(fid); return n; }),
  }), [ids, isCustomer, reload]);
  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  return useContext(WishlistContext);
}

/** Toggle for a product row / detail ({id, family_id}); guests go to sign in. */
export function useWishToggle() {
  const w = useWishlist();
  const navigate = useNavigate();
  return async (p) => {
    if (!w.isCustomer) { navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`); return; }
    const fid = p.family_id || p.id;
    const saved = w.ids.has(fid);
    w.setSaved(fid, !saved);
    try {
      if (saved) await api.delete(`/customer/wishlist/${p.id}`);
      else { await api.post("/customer/wishlist", { catalog_product_id: p.id }); toast.success("Saved to your wishlist"); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      w.reload();
    }
  };
}
